import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  Plane, 
  Users, 
  Calendar, 
  TrendingUp, 
  LogOut, 
  Bell,
  DollarSign,
  Plus,
  Eye,
  UserPlus,
  Calculator,
  AlertTriangle,
  MessageSquare,
  Key,
  FileText,
  Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({});
  const [myTrips, setMyTrips] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch stats
      const [statsRes, tripsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/trips/with-details`),
        axios.get(`${API}/notifications/payment-deadlines`)
      ]);

      setStats(statsRes.data);
      setMyTrips(tripsRes.data.slice(0, 5)); // Show last 5 trips
      setNotifications(notificationsRes.data.notifications.slice(0, 3)); // Show top 3 notifications
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 sm:py-0 sm:h-16 gap-4 sm:gap-0">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_e899d787-232a-49fb-887e-eb0377fd3380/artifacts/dy8a9fcf_trib%C3%B9%20in%20viaggio.png" 
                alt="Tribù in Viaggio" 
                className="h-10 sm:h-12 w-auto mr-3"
              />
              <h1 className="text-lg sm:text-xl font-bold text-slate-800">
                <span className="hidden sm:inline">Area Agente</span>
                <span className="sm:hidden">Agente</span>
              </h1>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <Link to="/notifications">
                <Button variant="outline" size="sm" className="relative">
                  <Bell className="w-4 h-4" />
                  {notifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                      {notifications.length}
                    </span>
                  )}
                </Button>
              </Link>
              <span className="text-xs sm:text-sm text-slate-600 hidden md:inline">
                {user?.first_name} {user?.last_name}
              </span>
              <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs sm:text-sm">
                Agent
              </Badge>
              <Link to="/change-password">
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  <span className="hidden md:inline">Password</span>
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className="flex items-center gap-2 text-sm sm:text-base px-2 sm:px-4"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Esci</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-2">
            Dashboard Agente
          </h2>
          <p className="text-slate-600">
            Gestisci i tuoi clienti e viaggi in modo professionale con Tribù in Viaggio
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-blue-100">
                I Miei Viaggi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Plane className="h-8 w-8 text-blue-200 mr-3" />
                <div className="text-2xl font-bold">{stats.my_trips || 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-100">
                Viaggi Confermati
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-green-200 mr-3" />
                <div className="text-2xl font-bold">{stats.confirmed_trips || 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-purple-100">
                Viaggi Completati
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-purple-200 mr-3" />
                <div className="text-2xl font-bold">{stats.completed_trips || 0}</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Agent Actions */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Strumenti Agente</CardTitle>
            <CardDescription>
              Gestisci i tuoi clienti e viaggi
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/manage-trips">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100"
                  variant="outline"
                >
                  <Plus className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Nuovo Viaggio</div>
                    <div className="text-sm text-teal-600">Crea viaggio per cliente</div>
                  </div>
                </Button>
              </Link>

              <Link to="/clients">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                  variant="outline"
                >
                  <Users className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">I Miei Clienti</div>
                    <div className="text-sm text-blue-600">Gestisci portafoglio clienti</div>
                  </div>
                </Button>
              </Link>

              <Link to="/commission-calculator">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                  variant="outline"
                >
                  <Calculator className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Calcola Commissioni</div>
                    <div className="text-sm text-green-600">Strumento calcolo rapido</div>
                  </div>
                </Button>
              </Link>

              <Link to="/deposit-calculator" data-testid="deposit-calculator-nav-link">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                  variant="outline"
                >
                  <Wallet className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Calcola Acconto Cliente</div>
                    <div className="text-sm text-amber-600">Acconto + volo da bonificare</div>
                  </div>
                </Button>
              </Link>

              <Link to="/financial-reports">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100"
                  variant="outline"
                >
                  <DollarSign className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Le Mie Commissioni</div>
                    <div className="text-sm text-purple-600">Report personali</div>
                  </div>
                </Button>
              </Link>

              <Link to="/calendar">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100"
                  variant="outline"
                >
                  <Calendar className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Calendario Viaggi</div>
                    <div className="text-sm text-orange-600">Pianifica e visualizza</div>
                  </div>
                </Button>
              </Link>

              <Link to="/quotes">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                  variant="outline"
                >
                  <FileText className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">I Miei Preventivi</div>
                    <div className="text-sm text-indigo-600">Crea preventivi professionali</div>
                  </div>
                </Button>
              </Link>

              <Link to="/notifications">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                  variant="outline"
                >
                  <Bell className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Scadenze</div>
                    <div className="text-sm text-red-600">Pagamenti e promemoria</div>
                  </div>
                </Button>
              </Link>

              <Link to="/quote-requests">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100"
                  variant="outline"
                >
                  <MessageSquare className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Richieste Preventivo</div>
                    <div className="text-sm text-teal-600">Richieste dai clienti</div>
                  </div>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* My Recent Trips */}
          <Card>
            <CardHeader>
              <CardTitle>I Miei Viaggi Recenti</CardTitle>
              <CardDescription>
                Ultimi viaggi che hai gestito
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {myTrips.length > 0 ? myTrips.map((tripData) => (
                  <div key={tripData.trip.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-800">{tripData.trip.title}</h4>
                      <p className="text-sm text-slate-600">{tripData.trip.destination}</p>
                      <p className="text-xs text-slate-500">
                        Cliente: {tripData.client?.first_name} {tripData.client?.last_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge 
                        variant={tripData.trip.status === 'active' ? 'default' : 'secondary'}
                        className="mb-2"
                      >
                        {tripData.trip.status}
                      </Badge>
                      <Link to={`/trips/${tripData.trip.id}`}>
                        <Button size="sm" variant="outline">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8">
                    <Plane className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                    <p className="text-slate-500">Nessun viaggio ancora</p>
                    <Link to="/manage-trips">
                      <Button className="mt-4" size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Crea il primo viaggio
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* My Notifications */}
          <Card>
            <CardHeader>
              <CardTitle>Le Mie Notifiche</CardTitle>
              <CardDescription>
                Scadenze e avvisi per i tuoi clienti
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {notifications.length > 0 ? notifications.map((notification) => (
                  <div key={notification.id} className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-slate-800">{notification.title}</h4>
                      <p className="text-sm text-slate-600">{notification.message}</p>
                      <p className="text-xs text-slate-500">
                        Scade in {notification.days_until_due} giorni
                      </p>
                    </div>
                    <Badge 
                      variant={notification.priority === 'high' ? 'destructive' : 'secondary'}
                    >
                      {notification.priority}
                    </Badge>
                  </div>
                )) : (
                  <div className="text-center py-8">
                    <Bell className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                    <p className="text-slate-500">Nessuna notifica urgente</p>
                    <p className="text-xs text-slate-400">Tutti i pagamenti sono in regola</p>
                  </div>
                )}
                {notifications.length > 0 && (
                  <Link to="/notifications">
                    <Button variant="outline" className="w-full">
                      Vedi tutte le notifiche
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AgentDashboard;